import { useState, useEffect } from 'react';
import { Menu, X, User, LayoutDashboard, Bookmark, Settings, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import { logoutUser } from '@/features/auth/authSlice';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getProfilePicture } from '@/services/profileService';
import { motion, AnimatePresence } from 'framer-motion';

'use client';

interface NavbarProps {
  isScrolled: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
}

const Navbar = ({ isScrolled, isMenuOpen, setIsMenuOpen }: NavbarProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [profilePic, setProfilePic] = useState('https://github.com/shadcn.png');

  // Check if user has admin role
  const isAdmin = user?.roles?.some((role) => ['admin', 'superadmin'].includes(role));

  useEffect(() => {
    const loadProfilePicture = async () => {
      if (user?.profilePhoto) {
        try {
          const result = await getProfilePicture(user.profilePhoto);
          const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
          setProfilePic(imageUrl);
        } catch (error) {
          console.error('Error loading profile picture:', error);
          setProfilePic('https://github.com/shadcn.png');
        }
      }
    };

    loadProfilePicture();
  }, [user]);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast({
        title: 'Logged out successfully',
        description: 'You have been logged out of your account.',
      });
      setIsMenuOpen(false); // Close menu on logout
      navigate('/signin');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to logout. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Function to close menu and navigate
  const handleLinkClick = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (!user) return 'CN';
    const name = user?.email || 'User';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-md' : 'bg-white/70 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <h1 className="text-2xl font-playfair font-bold text-primary">LearnHub</h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
            >
              Home
            </Link>
            <Link
              to="/courses"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
            >
              Courses
            </Link>
            <Link
              to="/providers"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
            >
              Providers
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
            >
              About
            </Link>
          </nav>

          {/* Desktop: Avatar Dropdown or Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative h-10 w-10 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    aria-label="User menu"
                  >
                    <Avatar className="w-full h-full">
                      <AvatarImage
                        src={profilePic}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                      <AvatarFallback className="bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 p-2"
                >
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 rounded-md hover:bg-primary/5 hover:text-primary focus:outline-none focus:bg-primary/5 cursor-pointer transition-colors"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 rounded-md hover:bg-primary/5 hover:text-primary focus:outline-none focus:bg-primary/5 cursor-pointer transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate('/saved-trainings')}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 rounded-md hover:bg-primary/5 hover:text-primary focus:outline-none focus:bg-primary/5 cursor-pointer transition-colors"
                  >
                    <Bookmark className="h-4 w-4 mr-2" />
                    Saved Trainings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/settings')}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 rounded-md hover:bg-primary/5 hover:text-primary focus:outline-none focus:bg-primary/5 cursor-pointer transition-colors"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 rounded-md hover:bg-red-50 focus:outline-none focus:bg-red-50 cursor-pointer transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/signin">
                  <Button
                    variant="outline"
                    size="sm"
                    className="hover:bg-primary/10 transition-colors"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="hover:bg-primary-dark transition-colors">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile: Hamburger Button */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} className="text-gray-700" /> : <Menu size={24} className="text-gray-700" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden bg-white shadow-lg border-t border-gray-100"
          >
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
              >
                Home
              </Link>
              <Link
                to="/courses"
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
              >
                Courses
              </Link>
              <Link
                to="/providers"
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
              >
                Providers
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
              >
                About
              </Link>
              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </Link>
                  )}
                  <Link
                    to="/saved-trainings"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                  >
                    <Bookmark className="h-4 w-4 mr-2" />
                    Saved Trainings
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full text-left px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="pt-2 space-y-2 px-3">
                  <Link
                    to="/signin"
                    onClick={() => setIsMenuOpen(false)}
                    className="block"
                  >
                    <Button
                      variant="outline"
                      className="w-full hover:bg-primary/10 transition-colors"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="block"
                  >
                    <Button className="w-full hover:bg-primary-dark transition-colors">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;